import type { ThemeColorModes } from '@atlaskit/tokens';

export const COLOR_MODE_STORAGE_KEY = 'switcheroo-color-mode-preference-v1';

export const COLOR_MODE_PREFERENCES = ['auto', 'light', 'dark'] as const satisfies readonly ThemeColorModes[];

export type ColorModePreference = (typeof COLOR_MODE_PREFERENCES)[number];
export type ResolvedColorMode = Exclude<ColorModePreference, 'auto'>;

const DEFAULT_COLOR_MODE_PREFERENCE: ColorModePreference = 'auto';

export const getSystemColorMode = (): ResolvedColorMode => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const resolveColorModePreference = (
    preference: ColorModePreference,
    systemColorMode: ResolvedColorMode = getSystemColorMode()
): ResolvedColorMode => (preference === 'auto' ? systemColorMode : preference);

const isColorModePreference = (value: string | null): value is ColorModePreference =>
    COLOR_MODE_PREFERENCES.some((preference) => preference === value);

export const getColorModePreference = (): ColorModePreference => {
    try {
        const preference = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
        return isColorModePreference(preference) ? preference : DEFAULT_COLOR_MODE_PREFERENCE;
    } catch {
        return DEFAULT_COLOR_MODE_PREFERENCE;
    }
};

export const setColorModePreference = (preference: ColorModePreference): boolean => {
    try {
        window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
        return true;
    } catch {
        return false;
    }
};
